import React, { useMemo } from 'react'
import { ProgressBar } from "react-step-progress-bar";
import { useNavigate, Link, Outlet, useParams } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import SupplierTalentWelcome from "./SupplierTalentWelcome";
import ResumeImportSelection from "../talent-application/components/ResumeImportSelection";
import SpinLoader from "../../components/SpinLoader";
import ApplicationStepper from '../talent-application/components/ApplicationStepper';
import { ArrowLeft, Sparkles } from 'lucide-react';
import TalentStatusPage from '../talent-application/journey/TalentStatusPage';

const ProfileApprovalIntro = (props) => {
    const { auth } = useAuth();

    // Not live yet: the "You're approved" status screen (same design as the login screens)
    if (!auth?.isProfileApproved?.()) {
        return <TalentStatusPage variant="approvedNotStarted" />;
    }

    return (
        <div className="welcome-panel">
            {/* <div className="welcome-top text-center">
                <h1>Hello {auth?.first_name}</h1>
                <h1>Welcome to Wripple!</h1>
                <p className="welcome-text">Congratulations on being an approved talent member.</p>
                <img src="/images/talent-welcome/teamwork.png" className="w-full max-w-[450px] mx-auto -mt-6"/>
            </div> */}

            <div className="welcome-top text-center">
                <div className="pb-8">
                    <ApplicationStepper />
                </div>

                <h3 className="text-3xl font-bold">Hey {auth?.first_name},</h3>
                <h1 className="text-2xl font-bold mb-0">Welcome to Wripple!</h1>

                <p className="text-lg pt-4 m-0">Your application is approved, but clients can't see you yet</p>
                <p className="text-gray-500 pt-2 m-0">One step left: build your profile and submit it for review</p>
                {/* <p className="welcome-text">Congratulations on being an approved talent member.</p> */}
                <img src="/images/talent-welcome/teamwork.png" className="w-full max-w-[450px] mx-auto -mt-6" />
            </div>

            <div className="welcome-bottom pb-5 md:flex">
                <div className="w-full text-center md:w-1/2">
                    {/* <p className="m-0 welcome-text">Now let's get to work.</p> */}
                    <p className="font-bold">Once approved, you'll unlock the marketplace</p>
                </div>
                <div className="mt-4 w-full flex justify-center md:justify-end md:w-1/2 md:mt-0">
                    <Link className="btn btn-primary" to={auth.isProfileApproved() ? `/talent/welcome/platform-highlights` : `/talent/welcome/next-steps`}>
                        Next
                    </Link>
                </div>
            </div>
        </div>
    )
}
const ProfileApprovalPlatformHighlights = (props) => {
    const { auth } = useAuth();

    const cards = [
        { title: 'Manage your\nprofile', img_src: 'profile' },
        { title: 'Let us know\nyour availability', img_src: 'calendar' },
        { title: 'Browse our\nJob Board', img_src: 'job-board' },
        { title: 'Pursue freelance\nprojects', img_src: 'freelance' },
        { title: 'Track payments,\ncontracts & invoices', img_src: 'payments' },
        { title: 'Refer your friends', img_src: 'friends', sub_title: '(earn extra cash)' },
    ]
    return (
        <div className="welcome-panel platform-features-panel">

            <div className="welcome-top text-center">
                <div className="pb-8">
                    <ApplicationStepper />
                </div>

                <h1 className="welcome-text">Remember Wripple is Entirely Free to Use. Below are highlights of
                    current platform features, and many more are on the way in upcoming releases.</h1>

                <div className="welcome-icons-container">

                    {cards.map(card => {
                        return (
                            <div className="w-1/2 md:w-1/3" key={card.img_src}>
                                <div className="welcome-icon-card">
                                    <img alt={card.title} src={`/images/talent-welcome/${card.img_src}.png`} />
                                    <p>{card.title}</p>
                                    {card.sub_title && (
                                        <p className="icon-card-subtitle">{card.sub_title}</p>
                                    )}
                                </div>

                            </div>
                        )
                    })}

                </div>
            </div>

            <div className="welcome-bottom">
                <div className="w-full flex justify-center lg:justify-end">
                    <Link className="btn btn-primary" to="/talent/welcome/next-steps">
                        Next
                    </Link>
                </div>
            </div>
        </div>
    )
}

const ProfileApprovalResumeImport = (props) => {
    const { auth, isUpdating, talentUpdate, markTalentWelcomeAsViewed } = useAuth();
    const navigate = useNavigate();

    const continueWithProfile = async () => {
        await markTalentWelcomeAsViewed();
        return navigate({
            pathname: "/talent/application"
        }, { replace: true })
    }
    const persistImport = async (params) => {
        let didImport = await talentUpdate(params)

        if (didImport) {
            TB.toast('Profile Updated!');
            return continueWithProfile()
        } else TB.showServerErrorToast();
    }

    return (
        <SpinLoader isLoading={isUpdating} className="welcome-panel platform-features-panel">
            <div className="welcome-top text-center">
                <div className="hidden md:block pb-8">
                    <ApplicationStepper />
                </div>
                <div className="flex justify-start">
                    <Link to="/talent/welcome" className="inline-flex items-center gap-1 text-xs md:text-sm text-gray-500 hover:text-gray-900 hover:no-underline">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </Link>
                </div>
                <p className="m-0 mt-2 inline-flex items-center gap-1.5 text-[11px] md:text-xs font-bold uppercase tracking-wider text-secondary">
                    <Sparkles className="h-3.5 w-3.5" /> Quick start
                </p>
                <h2 className="mt-2 mb-0 text-xl leading-tight md:text-2xl font-bold text-gray-900">
                    Fill your profile from your resume
                </h2>
                <p className="mt-2 mb-3 text-sm md:text-base text-gray-600">
                    Choose what to import. You can edit everything afterwards.
                </p>
            </div>
            <div className="welcome-bottom pb-5 md:flex [&_.mt-4]:mt-0 justify-center items-center flex-col">
                <ResumeImportSelection onSubmit={persistImport} isDisabled={props.disabled}>
                    <button className="btn btn-dark md:w-3/12"
                        onClick={continueWithProfile}>
                        Skip, start building profile
                    </button>
                </ResumeImportSelection>
            </div>
        </SpinLoader>
    )
}


const ProfileApprovalNextSteps = (props) => {
    // Merged into the single welcome screen; kept as an alias for existing links
    return <TalentStatusPage variant="approvedNotStarted" />;
};

const TalentProfileApprovedDetails = (props) => {
    const { auth, markTalentWelcomeAsViewed, appropriate } = useAuth();
    const progress = useMemo(() => auth?.profileProgress(), [auth]);
    const navigate = useNavigate();

    return (
        <div className="welcome-panel">

            <div className="welcome-top">
                <div className="pb-8">
                    <ApplicationStepper />
                </div>

                <h1 className="text-2xl font-bold mb-0">Winning work starts with a great profile</h1>

                <div className="panel-progress mt-3">
                    <p className="progress-title m-0">{`${progress}% of setup complete`}</p>
                    <ProgressBar
                        filledBackground={TB.config('color.light_green')}
                        height={20}
                        percent={progress}
                        unfilledBackground={TB.config('color.gray')}
                    />
                </div>
            </div>

            <div className="winning-work-actions">
                <div className="welcome-actions-section">
                    <img src="/images/talent-welcome/teamwork-writing.png" className="img-fluid block mx-auto" />

                    <button
                        className="btn btn-primary"
                        onClick={async () => {
                            await markTalentWelcomeAsViewed();
                            return navigate({
                                pathname: "/talent/application/bio?importResume=1",
                                // search: '?uploadResume=true'
                            }, { replace: true })
                        }}
                    >
                        Update Profile Now
                    </button>
                </div>

                <div className="divider" />

                <div className="welcome-actions-section">
                    <img src="/images/talent-welcome/dashboard-preview.png" className="img-fluid block mx-auto" />

                    <button className="btn btn-primary"
                        onClick={async () => {
                            await markTalentWelcomeAsViewed();
                            return navigate(appropriate(), { replace: true })
                        }}>
                        Skip to My Dashboard
                    </button>
                    <p className="actions-hint">I'll make profile updates later</p>
                </div>
            </div>

        </div>
    )
}

const TalentProfileApprovalWalkthrough = (props) => {

    const { auth, markTalentWelcomeAsViewed, shouldSeeTalentWelcome, appropriate } = useAuth();

    const { "*": params } = useParams();

    if (auth.isTalentSupplierResource()) {
        return <SupplierTalentWelcome />
    }

    // The "You're approved" status screen brings its own branded canvas
    if (!auth.isProfileApproved() && (!params || params === 'next-steps')) {
        return <Outlet />;
    }

    return (
        <div className={'talent-portal-page'}>
            {/* Slim brand bar; the journey stepper now lives inside each card */}
            <div className="w-full bg-white border-b border-gray-200 px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
                <img src={TB.logoBrandImagePath()} alt={laravel.platform_name} className="h-6 md:h-8 w-auto" />
                <span className="text-[11px] md:text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {auth?.isProfileApproved?.() ? 'Welcome to Wripple' : <>Step 3 of 4 &middot; Build your profile</>}
                </span>
            </div>

            <div className="talent-portal-welcome">
                <div className="container mx-auto">
                    <Outlet />
                </div>
            </div>
        </div>
    )
}

export default TalentProfileApprovalWalkthrough;

export {
    ProfileApprovalIntro,
    ProfileApprovalPlatformHighlights,
    ProfileApprovalResumeImport,
    ProfileApprovalNextSteps,
    TalentProfileApprovedDetails
}